import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { RefreshCw, Check, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { fmt } from "../../lib/money";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function statusBadge(entry) {
  if (entry.ok) {
    return (
      <span className="inline-flex items-center gap-1 bg-[#00A355] text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1">
        <Check className="w-3 h-3" /> {entry.status_code}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 bg-[#FF2A00] text-white text-[10px] uppercase tracking-widest font-bold px-2 py-1">
      <X className="w-3 h-3" /> {entry.status_code || "ERR"}
    </span>
  );
}

export default function AdminTrackmatrix() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [smokeRunning, setSmokeRunning] = useState(false);
  const [retryOrderId, setRetryOrderId] = useState("");
  const [retryRunning, setRetryRunning] = useState(false);
  const [retryResult, setRetryResult] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/trackmatrix/log?limit=25");
      setEntries(data.entries || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runSmoke = async () => {
    setSmokeRunning(true);
    try {
      const { data } = await api.post("/admin/trackmatrix/test");
      if (data.status_code >= 200 && data.status_code < 300) {
        toast.success(`Smoke test OK · ${data.status_code}`);
      } else {
        toast(`Smoke test returned ${data.status_code} — see log`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Smoke test failed");
    } finally {
      setSmokeRunning(false);
    }
  };

  const runRetry = async () => {
    const id = (retryOrderId || "").trim();
    if (!id) { toast.error("Enter an order id"); return; }
    setRetryRunning(true);
    setRetryResult(null);
    try {
      const { data } = await api.post("/admin/trackmatrix/retry", { order_id: id });
      setRetryResult(data);
      if (!data.ok) {
        toast(data.reason || "Order has no tm_ref");
      } else if (data.log?.ok) {
        toast.success(`Re-fired · ${data.log.status_code}`);
      } else {
        toast(`Re-fired · ${data.log?.status_code || "no response"} — see result`);
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Retry failed");
      setRetryResult({ ok: false, reason: err?.response?.data?.detail || "Retry failed" });
    } finally {
      setRetryRunning(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display font-black text-4xl tracking-tight">Trackmatrix conversions</h1>
          <p className="mt-2 text-sm text-neutral-600">
            Last 25 conversion postbacks sent to trackmatrix.net. Use the smoke test to verify the integration without placing a real order.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            data-testid="tm-refresh-btn"
            variant="outline"
            onClick={load}
            disabled={loading}
            className="rounded-none border-black h-10 px-4"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            data-testid="tm-smoke-btn"
            onClick={runSmoke}
            disabled={smokeRunning}
            className="rounded-none bg-black hover:bg-neutral-800 h-10 px-4 font-semibold"
          >
            <Zap className="w-4 h-4 mr-2" /> {smokeRunning ? "Sending…" : "Send smoke test"}
          </Button>
        </div>
      </div>

      {/* Retry-by-order-id diagnostic */}
      <div className="mt-6 border border-black p-4 bg-neutral-50">
        <div className="text-xs uppercase tracking-widest font-bold text-neutral-700 mb-2">Retry conversion by order ID</div>
        <div className="flex gap-2">
          <input
            data-testid="tm-retry-input"
            value={retryOrderId}
            onChange={(e) => setRetryOrderId(e.target.value)}
            placeholder="ord_xxxxxxxxxxxx (or last 8 chars)"
            className="flex-1 h-10 px-3 border border-black rounded-none font-mono text-sm"
          />
          <Button
            data-testid="tm-retry-btn"
            onClick={runRetry}
            disabled={retryRunning || !retryOrderId.trim()}
            className="rounded-none bg-[#0033FF] hover:bg-[#0022CC] h-10 px-4 font-semibold"
          >
            {retryRunning ? "Re-firing…" : "Retry"}
          </Button>
        </div>
        {retryResult && (
          <pre data-testid="tm-retry-result" className="mt-3 p-3 bg-white border border-neutral-300 text-xs overflow-auto max-h-60">
{JSON.stringify(retryResult, null, 2)}
          </pre>
        )}
      </div>

      {/* Patch tm_ref onto a ref-less order (e.g. Facebook in-app browser dropped the cookie) */}
      <PatchTmRefBox />

      {/* Debug: inspect tm_ref attribution sources for any order */}
      <DebugOrderBox />

      <div className="mt-8 border border-neutral-200 overflow-x-auto">
        {entries.length === 0 ? (
          <div className="p-10 text-center text-neutral-500" data-testid="tm-empty">
            No conversions yet. Once a shopper with a `?ref=` cookie places an order, it'll show up here.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                {["When", "Status", "Ref", "Order", "Total", "Commission", "Items", "Response"].map((h) => (
                  <th key={h} className="text-left px-4 py-3 text-xs uppercase tracking-widest text-neutral-500 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.log_id} data-testid={`tm-log-row-${e.log_id}`} className="border-b border-neutral-200 last:border-b-0 align-top">
                  <td className="px-4 py-3 whitespace-nowrap text-neutral-600">
                    {fmtTime(e.created_at)}
                    {e.is_smoke_test && (
                      <div className="text-[10px] uppercase tracking-widest text-[#0033FF] font-semibold mt-1">smoke</div>
                    )}
                  </td>
                  <td className="px-4 py-3">{statusBadge(e)}</td>
                  <td className="px-4 py-3 font-mono text-xs">{e.ref}</td>
                  <td className="px-4 py-3 font-mono text-xs">{e.order_id}</td>
                  <td className="px-4 py-3 font-display font-bold">{fmt(e.total)}</td>
                  <td className="px-4 py-3 text-[#00A355] font-semibold">{e.commission_total > 0 ? fmt(e.commission_total) : "—"}</td>
                  <td className="px-4 py-3 text-neutral-600">{e.items_count}</td>
                  <td className="px-4 py-3 text-neutral-600 max-w-[320px]">
                    <code className="text-[11px] break-all">{e.response || "—"}</code>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function PatchTmRefBox() {
  const [orderId, setOrderId] = useState("");
  const [ref, setRef] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!orderId.trim() || !ref.trim()) {
      toast.error("Both order id and ref are required");
      return;
    }
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/trackmatrix/patch", {
        order_id: orderId.trim(),
        tm_ref: ref.trim(),
      });
      setResult(data);
      if (data.log?.ok) {
        toast.success(`Patched + sent · ${data.log.status_code}`);
      } else {
        toast(`Returned ${data.log?.status_code || "no response"} — see result`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Patch failed");
      setResult({ ok: false, reason: err?.response?.data?.detail || "Patch failed" });
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="mt-4 border border-[#FF2A00] p-4 bg-[#FFF5F2]">
      <div className="text-xs uppercase tracking-widest font-bold text-[#FF2A00] mb-1">Patch tm_ref onto an organic order</div>
      <p className="text-xs text-neutral-600 mb-2">Use this when an order was placed without an affiliate cookie (Facebook in-app browser, cleared cookies, cross-device flow). Sets the ref + fires the conversion.</p>
      <form onSubmit={onSubmit} className="grid sm:grid-cols-[1fr_1fr_auto] gap-2">
        <input
          data-testid="tm-patch-order"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          placeholder="ord_xxxxxxxxxxxx (or last 8 chars)"
          className="h-10 px-3 border border-black rounded-none font-mono text-sm"
        />
        <input
          data-testid="tm-patch-ref"
          value={ref}
          onChange={(e) => setRef(e.target.value)}
          placeholder="affiliate slug (e.g. slipper-dec507)"
          className="h-10 px-3 border border-black rounded-none font-mono text-sm"
        />
        <button
          type="submit"
          data-testid="tm-patch-btn"
          disabled={running || !orderId.trim() || !ref.trim()}
          className="h-10 px-4 bg-[#FF2A00] hover:bg-[#cc2200] text-white font-semibold disabled:opacity-50"
        >
          {running ? "Patching…" : "Patch + send"}
        </button>
      </form>
      {result && (
        <pre data-testid="tm-patch-result" className="mt-3 p-3 bg-white border border-neutral-300 text-xs overflow-auto max-h-60">
{JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}


function DebugOrderBox() {
  const [orderId, setOrderId] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    const id = orderId.trim();
    if (!id) { toast.error("Enter an order id"); return; }
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.get(`/admin/trackmatrix/order/${encodeURIComponent(id)}`);
      setResult(data);
      if (!data.ok) {
        toast(data.reason || "Lookup failed");
      } else if (!data.tm_ref) {
        toast(`Order found — tm_ref is NULL (all 4 sources failed)`);
      } else {
        toast.success(`tm_ref = ${data.tm_ref}`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Lookup failed");
      setResult({ ok: false, reason: err?.response?.data?.detail || "Lookup failed" });
    } finally {
      setRunning(false);
    }
  };

  const sources = result?.tm_ref_sources || {};
  const tiers = [
    { key: "payload", label: "Payload (Checkout.jsx body)" },
    { key: "js_cookie", label: "tm_ref cookie (JS-readable)" },
    { key: "httponly_cookie", label: "tm_ref_srv cookie (HttpOnly — FB-IAB-safe)" },
    { key: "user_record", label: "users.tm_ref (server-side fallback)" },
  ];

  return (
    <div className="mt-4 border border-[#0033FF] p-4 bg-[#F0F4FF]">
      <div className="text-xs uppercase tracking-widest font-bold text-[#0033FF] mb-1">Debug attribution sources for an order</div>
      <p className="text-xs text-neutral-600 mb-2">
        Look up any order and see exactly which of the 4 tm_ref tiers fired. If all 4 are null, the affiliate cookie never reached checkout — share the order id with support to investigate the user's browser context.
      </p>
      <form onSubmit={onSubmit} className="grid sm:grid-cols-[1fr_auto] gap-2">
        <input
          data-testid="tm-debug-order"
          value={orderId}
          onChange={(e) => setOrderId(e.target.value)}
          placeholder="ord_xxxxxxxxxxxx"
          className="h-10 px-3 border border-black rounded-none font-mono text-sm"
        />
        <button
          type="submit"
          data-testid="tm-debug-btn"
          disabled={running || !orderId.trim()}
          className="h-10 px-4 bg-[#0033FF] hover:bg-[#0022CC] text-white font-semibold disabled:opacity-50"
        >
          {running ? "Looking up…" : "Inspect"}
        </button>
      </form>
      {result?.ok && (
        <div className="mt-3 bg-white border border-neutral-300 p-3">
          <div className="text-xs font-mono mb-2">
            <span className="text-neutral-500">order:</span> {result.order_id} ·{" "}
            <span className="text-neutral-500">total:</span> {fmt(result.total)} ·{" "}
            <span className="text-neutral-500">customer:</span> {result.customer?.email || "?"}
          </div>
          <div className="text-xs font-mono mb-3">
            <span className="text-neutral-500">resolved tm_ref:</span>{" "}
            {result.tm_ref ? (
              <code className="bg-[#00A355] text-white px-1.5 py-0.5">{result.tm_ref}</code>
            ) : (
              <code className="bg-[#FF2A00] text-white px-1.5 py-0.5">NULL — attribution lost</code>
            )}
            {result.tm_ref_patched_by && (
              <span className="ml-2 text-[#0033FF]">· (manually patched)</span>
            )}
          </div>
          <table className="w-full text-xs">
            <thead className="border-b border-neutral-300">
              <tr className="text-neutral-500 uppercase tracking-widest">
                <th className="text-left py-1.5 font-semibold">Tier</th>
                <th className="text-left py-1.5 font-semibold">Value at checkout time</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((t) => {
                const v = sources[t.key];
                return (
                  <tr key={t.key} className="border-b border-neutral-200 last:border-b-0">
                    <td className="py-1.5 pr-3">{t.label}</td>
                    <td className="py-1.5 font-mono">
                      {v ? (
                        <code className="bg-[#00A355]/15 text-[#00A355] px-1.5 py-0.5">{v}</code>
                      ) : (
                        <span className="text-neutral-400">null</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {Object.keys(sources).length === 0 && (
            <div className="mt-2 text-xs text-neutral-500 italic">
              No source snapshot recorded — this order was placed before debug logging was deployed.
            </div>
          )}
        </div>
      )}
      {result && !result.ok && (
        <pre className="mt-3 p-3 bg-white border border-neutral-300 text-xs">
{JSON.stringify(result, null, 2)}
        </pre>
      )}
    </div>
  );
}
